import i18n from '@/i18n';

/**
 * CHANGER DE LANGUE — le geste complet, et pourquoi il en faut un.
 *
 * ── LE DÉFAUT ────────────────────────────────────────────────────────────────
 *
 * Les trois sélecteurs appelaient `i18n.changeLanguage(code)` et s'arrêtaient
 * là. Le détecteur écrit bien le choix dans `localStorage` (`aikka-lang`), donc
 * « ça marche » — jusqu'au rechargement suivant, parce que l'ordre de détection
 * déclaré dans `i18n/index.ts` est :
 *
 *     ['querystring', 'localStorage', 'navigator', 'htmlTag']
 *
 * et `querystring` PASSE DEVANT. Ce n'est pas un réglage à corriger : il est
 * load-bearing pour le SEO. Le site sert fr/en/es depuis la MÊME URL, donc
 * `<link rel="alternate" hreflang>` n'aurait rien à désigner sans un `?lang=xx`
 * capable d'épingler une langue, et un robot — ou un humain venu d'un résultat
 * traduit — qui atterrit sur `?lang=en` doit recevoir de l'anglais.
 *
 * Mais la conséquence, elle, est un vrai bug d'usage : un visiteur arrivé sur
 * `…?lang=en` qui choisit « Español » obtient bien l'espagnol tout de suite, et
 * retrouve l'anglais à la première navigation complète — le paramètre est
 * toujours dans la barre d'adresse et il regagne à chaque fois. C'est
 * exactement « Permanence de la langue sélectionnée » (Notion, « Last push
 * before going live » → Interface).
 *
 * ── LE GESTE ─────────────────────────────────────────────────────────────────
 *
 * Un choix EXPLICITE fait deux choses, pas une : il change la langue, et il
 * retire de l'URL le `?lang=` qui la contredirait. `replaceState` et non
 * `pushState` : changer de langue n'est pas une navigation, et le bouton retour
 * ne doit pas ramener à la version précédente de la même page.
 *
 * Ce qui n'est PAS fait ici, délibérément : réécrire `?lang=` avec le nouveau
 * code. Ce paramètre est un outil de crawl, pas un état d'application ; le
 * laisser vivre à jamais dans l'URL d'un visiteur ferait de chaque lien qu'il
 * partage un lien épinglé sur SA langue.
 */
export function applyLanguage(code: string): void {
  void i18n.changeLanguage(code);

  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('lang')) return;
    url.searchParams.delete('lang');
    // `search` vaut '' quand il ne reste aucun paramètre, ce qui donne bien une
    // URL sans point d'interrogation orphelin.
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* URL illisible ou history indisponible : le changement de langue a eu
       lieu, c'est le nettoyage de l'URL qui est perdu. */
  }
}
